import Cookies from 'js-cookie';
import { JackpotStaticTexts } from './../../components/jackpots-wrapper/types';
import config from '../../config';
import {
  attachProperSizeForImages,
  filterCloseLiveTableGames,
  transformImage,
} from '../../model/algolia/utils';
import { DeviceSettings } from '../../ports-frontend/types';
import {
  AvailableGridTypes,
  CategoriesTypes,
  CategoriesWithContentTypes,
  CategoryBannerType,
  CategoryBannerWithContentType,
  CategoryBaseWithContentType,
} from '../../types/category/categoryTypes';
import { PortalVerticalGameTilePropTypes } from '../../types/gameTypes';
import {
  ExpectedTierType,
  FetchedTierType,
  JackpotSlot,
  JackpotSlotMapped,
} from '../../types/jackpotTypes';
import {
  CHUNK_SIZE_MAP,
  CONTEXT_ATTRIBUTES,
  GAMING_SEGMENTS_COOKIE_NAME,
  WEB_ATTRIBUTES,
} from './constants';
import {
  CallbackMode,
  LogicContext,
  UserSessionInfo,
  PartialEventData,
  DeviceOs,
} from './types';

type TextValues = {
  name: string;
  activeText: string;
};

export const isIframeWrapperEnabled = (): boolean => {
  return !!(
    config.features['iFrame'] && config.features['iFrame'].mode === 'enabled'
  );
};

export const getCallbackMode = (
  currentScreenSize: string,
  deviceInfo: DeviceSettings,
  user?: UserSessionInfo
): CallbackMode =>
  !isIframeWrapperEnabled() &&
  (!!(user && !user.loggedIn) ||
    (currentScreenSize === 'wide' && deviceInfo.deviceType === 'desktop'))
    ? 'event'
    : 'native';

export const trackingDisabled = (context: LogicContext) =>
  !context.features.tracking || context.features.tracking.mode === 'disabled';

export const trackingDelayed = () => {
  return config.features.tracking && config.features.tracking.delayed;
};

export const managePhrases = (
  oldPhrases: string[],
  newPhrase: string | string[],
  length: number
) => {
  let finalPhrases: string[] = [];
  if (typeof newPhrase === 'string') {
    finalPhrases = oldPhrases
      .filter(phrase => phrase !== newPhrase)
      .slice(0, length - 1);
    finalPhrases.unshift(newPhrase);
  } else {
    finalPhrases = oldPhrases;
    const cutted = newPhrase.slice(0, length - 1).reverse();
    cutted.forEach(phrase => {
      if (!finalPhrases.includes(phrase)) {
        finalPhrases.unshift(phrase);
      }
    });
    finalPhrases = finalPhrases.slice(0, length);
  }
  return finalPhrases;
};

export const groupItemsByTypes = (
  items: Array<PortalVerticalGameTilePropTypes>
) => {
  const grouppedItems: {
    [key: string]: Array<PortalVerticalGameTilePropTypes>;
  } = {
    All: items,
  };
  items.forEach(game => {
    if (game.type) {
      if (grouppedItems[game.type]) {
        grouppedItems[game.type].push(game);
      } else {
        grouppedItems[game.type] = [game];
      }
    }
  });

  return grouppedItems;
};

function convertArrayToObject(arr: any[], key: string) {
  return arr.reduce((acc, item) => {
    return {
      ...acc,
      [item[key]]: item,
    };
  }, {});
}

export const getNewGames = (
  items: Array<PortalVerticalGameTilePropTypes>
): { [key: string]: PortalVerticalGameTilePropTypes } => {
  return convertArrayToObject(items, 'id');
};

export const checkIfAllLiveGamesAreClosed = (
  games: PortalVerticalGameTilePropTypes[] = []
) => {
  const allClosedTableGames = games.every(
    (game: PortalVerticalGameTilePropTypes) => {
      return game.table_id && game.table?.online.open === false;
    }
  );
  return allClosedTableGames ? true : false;
};

export const getStartTimeTables = (
  games: PortalVerticalGameTilePropTypes[] = []
) => {
  const startTimes = games
    .map(game => game.table?.online)
    .filter(tableOnline => tableOnline?.open === false)
    .map(tableOnline => tableOnline?.startTime);

  const parseTimeToMinutes = (startTime?: string) => {
    if (!startTime) return 0;

    const times = startTime.split(':').map(Number);
    return times[0] * 60 + times[1];
  };

  if (!startTimes) return undefined;
  if (startTimes.length === 1) {
    return startTimes[0];
  } else {
    startTimes.sort((a, b) => parseTimeToMinutes(a) - parseTimeToMinutes(b));
  }

  return startTimes[0];
};

function Banner({
  category,
  windowInnerWidth,
  currentPersonalizedBanners,
}: {
  category: CategoryBannerType;
  windowInnerWidth: number;
  currentPersonalizedBanners?: LogicContext['currentPersonalizedBanners'];
}): CategoryBannerWithContentType {
  return {
    ...category,
    slides: category.bannerSlides.map(slide => ({
      type: 'Banner' as 'Banner',
      gridType: 'Banner',
      banner: {
        asset: slide.banner.asset && {
          ...slide.banner.asset,
          source: transformImage(
            slide.banner.asset.source,
            windowInnerWidth - 34
          ),
        },
        smallLargeAsset: slide.banner.smallLargeAsset && {
          ...slide.banner.smallLargeAsset,
          source: transformImage(
            slide.banner.smallLargeAsset.source,
            windowInnerWidth - 34
          ),
        },
        mediumAsset: slide.banner.mediumAsset && {
          ...slide.banner.mediumAsset,
          source: transformImage(
            slide.banner.mediumAsset.source,
            windowInnerWidth - 34
          ),
        },
        buttonText: slide.banner.buttonText,
        description: slide.banner.description,
        type: slide.banner.type,
        game: slide.banner.game,
        personalization: slide.banner.personalization && {
          ...slide.banner.personalization,
          rendered:
            currentPersonalizedBanners &&
            currentPersonalizedBanners[slide.banner.personalization.selector],
        },
        route_link: slide.banner.route_link,
        link: slide.banner.link,
        notIndexed: slide.banner.notIndexed,
      },
    })),
  };
}

export const divideGameListToSlides = ({
  gameList = [],
  type = 'Grid',
  jackpotSlot,
  unlimited,
}: {
  gameList?: PortalVerticalGameTilePropTypes[];
  type: AvailableGridTypes;
  jackpotSlot?: JackpotSlotMapped;
  unlimited?: boolean;
}): CategoryBaseWithContentType['slides'] => {
  type = CHUNK_SIZE_MAP[type] ? type : 'Grid';
  let chunk = CHUNK_SIZE_MAP[type].normal;
  const slides: {
    jackpotSlot?: any;
    gameList: PortalVerticalGameTilePropTypes[];
    type: AvailableGridTypes;
    gamesLimit?: number;
    slideInitialIndex: number;
  }[] = [];
  let slideType = type;
  let slideInitialIndex = 0;
  for (let i = 0, j = gameList.length; i < j; i += chunk) {
    chunk = CHUNK_SIZE_MAP[slideType].normal;
    switch (slideType) {
      case 'Jackpot Hero':
      case 'Jackpot Banner': {
        slides.push({
          type: slideType,
          gameList: gameList.slice(
            i,
            i === 0 ? CHUNK_SIZE_MAP[slideType].big : i + chunk
          ),
          gamesLimit:
            i === 0
              ? CHUNK_SIZE_MAP[slideType].big
              : CHUNK_SIZE_MAP[slideType].normal,
          jackpotSlot,
          slideInitialIndex,
        });
        slideType =
          slideType === 'Jackpot Banner' ? 'Game Dual' : 'Jackpot Nine';
        break;
      }

      case 'Game Banner': {
        if (!gameList[i].bannerImage) {
          const fallbackType = 'Game Dual';
          chunk = CHUNK_SIZE_MAP[fallbackType].normal;
          slides.push({
            type: 'Game Dual',
            gameList: gameList.slice(
              i,
              i === 0 ? CHUNK_SIZE_MAP[fallbackType].big : i + chunk
            ),
            gamesLimit:
              i === 0
                ? CHUNK_SIZE_MAP[fallbackType].big
                : CHUNK_SIZE_MAP[fallbackType].normal,
            slideInitialIndex,
          });
        } else {
          slides.push({
            type: slideType,
            gameList: gameList.slice(
              i,
              i === 0 ? CHUNK_SIZE_MAP[slideType].big : i + chunk
            ),
            gamesLimit:
              i === 0
                ? CHUNK_SIZE_MAP[slideType].big
                : CHUNK_SIZE_MAP[slideType].normal,
            slideInitialIndex,
          });
        }
        break;
      }
      case 'Dynamic Grid':
      case 'Collection': {
        slides.push({
          type: slideType,
          gameList: gameList,
          gamesLimit: gameList.length,
          slideInitialIndex,
        });
        chunk = gameList.length;
        break;
      }
      default: {
        slides.push({
          type: slideType,
          gameList: gameList.slice(
            i,
            i === 0 ? CHUNK_SIZE_MAP[slideType].big : i + chunk
          ),
          gamesLimit: unlimited
            ? undefined
            : i === 0
            ? CHUNK_SIZE_MAP[slideType].big
            : CHUNK_SIZE_MAP[slideType].normal,
          slideInitialIndex,
        });
        break;
      }
    }
    slideInitialIndex += chunk;
  }

  return slides;
};

function mapGamesWithTables(
  games: PortalVerticalGameTilePropTypes[],
  currentTables: LogicContext['currentTables'],
  locale?: string,
  currency?: string
) {
  if (currentTables && games) {
    return games.map((game: any) => ({
      ...game,
      table:
        game.table_id && currentTables[game.table_id]
          ? {
              ...currentTables[game.table_id],
              locale,
              currencyCode: currency,
            }
          : undefined,
    }));
  }

  return games;
}

export function getCategories({
  categories,
  currentJackpots,
  currentTables,
  currentPersonalizedBanners,
  windowInnerWidth = 400,
  locale,
  currency,
}: {
  categories: CategoriesTypes[];
  currentJackpots?: LogicContext['currentJackpots'];
  currentTables?: LogicContext['currentTables'];
  currentPersonalizedBanners?: LogicContext['currentPersonalizedBanners'];
  windowInnerWidth?: number;
  locale?: string;
  currency?: string;
}): CategoriesWithContentTypes[] {
  const gameDetailsInfo = config.features.gameDetails.mode === 'enabled';

  return categories.map((category: CategoriesTypes) => {
    switch (category.component_type) {
      case 'banner': {
        return {
          ...Banner({ category, windowInnerWidth, currentPersonalizedBanners }),
          gameDetailsInfo,
        };
      }
      case 'game_collection': {
        const gamesWithTables = mapGamesWithTables(
          category.games,
          currentTables,
          locale,
          currency
        );
        const filteredGames = attachProperSizeForImages(
          filterCloseLiveTableGames(gamesWithTables),
          windowInnerWidth
        );
        const slides = divideGameListToSlides({
          gameList: filteredGames,
          type: 'Collection' as 'Collection',
        });
        return {
          ...category,
          overlay: checkIfAllLiveGamesAreClosed(gamesWithTables),
          tableStartTime: getStartTimeTables(gamesWithTables),
          gameDetailsInfo,
          slides,
        };
      }
      case 'jackpot_grid': {
        const gamesWithTables = mapGamesWithTables(
          category.games,
          currentTables,
          locale,
          currency
        );
        const filteredGames = attachProperSizeForImages(
          filterCloseLiveTableGames(gamesWithTables),
          windowInnerWidth
        );
        const jackpotSlot = getJackpotProperties(
          category.jackpot_widget,
          currentJackpots,
          locale,
          currency
        );
        let slides = divideGameListToSlides({
          gameList: filteredGames,
          type: category.type,
          jackpotSlot,
        });

        if (!filteredGames.length && jackpotSlot) {
          slides = [
            {
              jackpotSlot,
              type: category.type,
              slideInitialIndex: 0,
            },
          ];
        }

        return {
          ...category,
          overlay: checkIfAllLiveGamesAreClosed(gamesWithTables),
          tableStartTime: getStartTimeTables(gamesWithTables),
          gameDetailsInfo,
          slides,
        };
      }
      case 'game_grid':
      default: {
        const gamesWithTables = mapGamesWithTables(
          category.games,
          currentTables,
          locale,
          currency
        );
        const filteredGames = attachProperSizeForImages(
          filterCloseLiveTableGames(gamesWithTables),
          windowInnerWidth
        );
        const slides = divideGameListToSlides({
          gameList: filteredGames,
          type: category.type,
        });
        return {
          ...category,
          overlay: checkIfAllLiveGamesAreClosed(gamesWithTables),
          tableStartTime: getStartTimeTables(gamesWithTables),
          gameDetailsInfo,
          slides,
        };
      }
    }
  });
}

export function removeQueryParams(url: string) {
  return url.split('?')[0];
}

export function getPreviousPageArray<T extends { pagePath: string }>({
  pageParams,
  previousPageArray = [],
  arraySizeLimit,
}: {
  pageParams: T;
  previousPageArray: T[];
  arraySizeLimit: number;
}): T[] {
  const pageIdArray = previousPageArray.slice(-arraySizeLimit);
  return pageIdArray.slice(-1)[0]?.pagePath === pageParams.pagePath
    ? pageIdArray
    : [...pageIdArray, pageParams];
}

export function getPromotionsPath(promotionSlug?: string): string {
  const basePath = config.base_path || '';
  const promoBasePath = `${basePath}${config.functionalities.promotions.path}`;
  //update promotions with current slug if passed
  return promotionSlug ? `${promoBasePath}/${promotionSlug}` : promoBasePath;
}

export function getProductContext(): string | null {
  return config.features.tracking.productContext;
}

// JACKPOTS
const getJackpotProperties = (
  jackpotSlot: JackpotSlot,
  currentJackpots?: LogicContext['currentJackpots'],
  locale?: string,
  currency?: string
): JackpotSlotMapped | undefined => {
  if (!jackpotSlot) return undefined;
  if (!currentJackpots) return undefined;
  const matchingJackpotValues = currentJackpots[jackpotSlot.provider];
  if (!matchingJackpotValues) return undefined;

  const { tiers, overlayTier } = extractTiersFromJackpotSlot(
    jackpotSlot,
    matchingJackpotValues,
    jackpotSlot.staticTexts
  );
  return {
    type: jackpotSlot.type,
    images: {
      logo: {
        source: transformImage(jackpotSlot.logo.source),
        alt: jackpotSlot.logo.alt,
      },
      background: {
        source: jackpotSlot.background?.normal
          ? transformImage(jackpotSlot.background?.normal)
          : { normal: '' },
      },
    },
    staticTexts: jackpotSlot.staticTexts,
    valueAnimationDuration: config.intervals.jackpotsCheck,
    tiers,
    overlayTier,
    locale,
    currencyCode: currency,
  };
};

const mappers = {
  'red-tiger': (
    tier: FetchedTierType,
    { name, activeText }: TextValues,
    index: number
  ): ExpectedTierType => {
    if (!tier) {
      return {
        name: '',
        curValue: 0,
        state: 'normal',
      };
    }
    const { previousPrize } = tier;
    return {
      name,
      curValue: tier.prize,
      state: previousPrize ? 'won' : index === 0 ? 'normal' : 'active',
      releaseTime: new Date(tier.targetTime!).toString(),
      releaseMessage: activeText,
      previousPrize,
    };
  },
  'vegas-millions': (
    tier: FetchedTierType,
    { name }: TextValues
  ): ExpectedTierType => {
    return {
      name,
      curValue: tier?.prize,
      state: (tier?.state?.toLowerCase() || 'active') as
        | 'normal'
        | 'active'
        | 'won',
    };
  },
};

const extractTiersFromJackpotSlot = (
  jackpotSlot: JackpotSlot,
  matchingJackpotValues: FetchedTierType[],
  staticTexts: JackpotStaticTexts
) => {
  const {
    first_jackpot_field,
    second_jackpot_field,
    third_jackpot_field,
    type,
  } = jackpotSlot;

  const mapper = mappers[type];

  const tiers = [
    first_jackpot_field,
    second_jackpot_field,
    third_jackpot_field,
  ].map((fieldConfig, index) => {
    const { selector, title, messages } = fieldConfig;
    const { key } = selector;

    const foundTier = matchingJackpotValues.find(
      item => item[key as keyof FetchedTierType] === selector.value
    );

    return mapper(
      foundTier!,
      { name: title, activeText: messages.active },
      index
    );
  });

  const wonTier = tiers.find(
    (tier: ExpectedTierType | undefined) => tier?.state?.toLowerCase() === 'won'
  );

  if (wonTier) {
    return {
      tiers,
      overlayTier: {
        name: wonTier.name,
        value: wonTier.curValue,
        message: staticTexts.winMessage,
        nextValue: wonTier.previousPrize!,
        nextJackpotName: staticTexts.nextJackpot,
        background: {
          source: transformImage(jackpotSlot.background!.won),
        },
      },
    };
  }

  return {
    tiers,
  };
};

//tmp solution for app specific params

const createUrlParams = (params: any) => {
  const urlParams = new URLSearchParams();
  Object.keys(params).forEach((key: string) => {
    const param = params[key];
    if (param) {
      const mappedKey = WEB_ATTRIBUTES.includes(key) ? `web.${key}` : key;

      if (Array.isArray(param)) {
        urlParams.append(`context[${mappedKey}]`, param.toString());
      } else {
        const paramKey = CONTEXT_ATTRIBUTES.includes(key)
          ? `context[${mappedKey}]`
          : mappedKey;

        urlParams.append(paramKey, param);
      }
    }
  });

  urlParams.sort();
  return urlParams.toString();
};

const mapUserDevice = (deviceType: string, deviceOs: string | null) => {
  if (deviceType === 'desktop') return 'web';
  else {
    if (deviceOs === 'android' || deviceOs === 'ios') return deviceOs;
    return 'mobile_web';
  }
};

const getSegmentsCookie = () =>
  Cookies.getJSON(GAMING_SEGMENTS_COOKIE_NAME)?.segments;

export function mapUrl(
  url: string,
  deviceType: 'mobile' | 'tablet' | 'desktop',
  deviceOs: DeviceOs
) {
  const specificParams = createUrlParams({
    ...config.launcher.staticUrlParams,
    agent: mapUserDevice(deviceType, deviceOs),
    segments: getSegmentsCookie(),
  });

  return `${config.launcher.apiUrl}${url}&${specificParams}&channel=${deviceType}`;
}

export function pathJoin(parts: Array<string | undefined>) {
  const replace = new RegExp('([^:])/{1,}', 'g');
  return parts
    .filter(path => !!path)
    .join('/')
    .replace(replace, '$1/');
}

export function createSearchQuery(params?: {
  [key: string]: string | number | boolean | string[] | undefined;
}): string {
  if (!params) return '';

  const urlParams = new URLSearchParams();
  Object.keys(params).forEach((key: string) => {
    const param = params[key];
    if (param !== undefined) {
      if (Array.isArray(param)) {
        param.forEach((paramItem: string) =>
          urlParams.append(`${key}[]`, paramItem)
        );
        if (param.length === 0) {
          urlParams.append(`${key}[]`, '');
        }
      } else {
        urlParams.append(key, param.toString());
      }
    }
  });

  return `?${urlParams.toString()}`;
}

export function extractNavigationLinksFromNavComponent(
  navigationComponent: {
    properties: {
      categories?: {
        items: {
          routes?: { route_link: string }[];
          hide_on?: Array<'Large' | 'Small'>;
          route_link: string;
        }[];
      }[];
    };
  },
  currentScreenSize: LogicContext['currentScreenSize']
): string[] {
  if (!navigationComponent) return [];

  return navigationComponent.properties.categories
    ? navigationComponent.properties.categories
        .map(category =>
          category.items
            .filter(item =>
              item.hide_on && currentScreenSize
                ? !item.hide_on.includes(
                    currentScreenSize === 'wide' ? 'Large' : 'Small'
                  )
                : true
            )
            .map(item =>
              item.routes?.length
                ? item.routes.map(i => i.route_link)
                : item.route_link
            )
        )
        .flat(3)
    : [];
}

export function omitKeys(
  keys: string[],
  data: PartialEventData
): PartialEventData {
  const filteredObj: PartialEventData = {};
  for (const item of Object.entries(data)) {
    if (!keys.includes(item[0])) {
      filteredObj[item[0]] = item[1];
    }
  }
  return filteredObj;
}
